import { FC, useEffect, useMemo, useState } from 'react'
import './verify.css'

// COMPONENTS
import { Column } from '@components/templates/column'
import AnchorLink from '@components/atoms/anchor-link'
import Button from '@components/atoms/button'
import OneTimePassword from '@/components/molecules/one-time-password'

// TYPES
import type { EmailCodeFactor, PhoneCodeFactor } from '@clerk/types'
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { links } from '@constants'
import { useAuthSettings } from '../_localStore/_hooks'
import { useSignIn, useSignUp, useUser } from '@clerk/clerk-react'
import { useInput } from '@hooks/inputs'
import * as Sentry from '@sentry/react'


type FormType = 'sms' | 'email'

const disableDuration = 30
const placeholder = '123456'
const otpLength = placeholder.length
const formId = 'auth-verify-form'

const AuthVerifyScreen: FC = () => {
    const { state: { isNewUser }, createToast } = useAuthSettings()
    const [formType, setFormType] = useState<FormType>('sms')
    const { signIn, setActive } = useSignIn()
    const { signUp } = useSignUp()
    const { isLoaded } = useUser()

    const [otp, otpProps] = useInput()
    const [currentCountDown, setCurrentCountDown] = useState(disableDuration)
    const [optSubmitEnabled, setOptSubmitEnabled] = useState(false)

    useEffect(() => {
        if (currentCountDown <= 0) {
            setOptSubmitEnabled(true)
            return
        }

        const intervalId = setInterval(() => {
            setCurrentCountDown(prevCount => prevCount - 1)
        }, 1000)

        return () => clearInterval(intervalId)
    }, [currentCountDown])

    const pseudoUser = useMemo(() => ({
        firstName: signIn?.userData?.firstName || signUp?.firstName || '',
        lastName: signIn?.userData?.lastName || signUp?.lastName || '',
        phoneNumber: signIn?.supportedIdentifiers.includes('phone_number')
            ? signIn.supportedFirstFactors?.find((sff) => sff.strategy === 'phone_code' && sff.primary) as PhoneCodeFactor | undefined
            : undefined,
        email: signIn?.supportedIdentifiers.includes('email_address')
            ? signIn.supportedFirstFactors?.find((sff) => sff.strategy === 'email_code' && sff.primary) as EmailCodeFactor | undefined
            : undefined
    }), [signIn, signUp])

    const isSubmitable = useMemo(() => { return otpProps.value.length === otpLength }, [otpProps.value])

    async function verifyCode(e?: React.FormEvent<HTMLFormElement>) {
        e?.preventDefault()
        Sentry.addBreadcrumb({ category: 'auth', message: 'verifyCode() started', level: 'info', data: { signInStatus: signIn?.status, isNewUser } })
        try {
            if (signIn?.status !== 'needs_identifier' && !isNewUser) {
                // Sign In Flow
                Sentry.addBreadcrumb({ category: 'auth', message: 'attempting SIGN IN flow', level: 'info' })
                const res = await signIn?.attemptFirstFactor({ strategy: formType === 'sms' ? 'phone_code' : 'email_code', code: otp })
                Sentry.addBreadcrumb({ category: 'auth', message: 'attemptFirstFactor result', level: 'info', data: { status: res?.status, sessionId: res?.createdSessionId } })
                if (res?.status === 'complete') {
                    Sentry.addBreadcrumb({ category: 'auth', message: 'calling activateSession()', level: 'info' })
                    await activateSession(res.createdSessionId)
                    Sentry.addBreadcrumb({ category: 'auth', message: 'activateSession() completed', level: 'info' })
                }
                else throw new Error(`Sign in failed with status: ${res?.status}`)
            } else {
                // Sign Up Flow
                Sentry.addBreadcrumb({ category: 'auth', message: 'attempting SIGN UP flow', level: 'info' })
                const res = await signUp?.attemptPhoneNumberVerification({ code: otp })
                Sentry.addBreadcrumb({ category: 'auth', message: 'attemptPhoneNumberVerification result', level: 'info', data: { status: res?.status, sessionId: res?.createdSessionId } })
                if (res?.status === 'complete') {
                    Sentry.addBreadcrumb({ category: 'auth', message: 'calling activateSession()', level: 'info' })
                    await activateSession(res.createdSessionId)
                    Sentry.addBreadcrumb({ category: 'auth', message: 'activateSession() completed', level: 'info' })
                }
                else throw new Error(`Sign up failed with status: ${res?.status}`)
            }
            Sentry.addBreadcrumb({ category: 'auth', message: 'verifyCode() finished successfully', level: 'info' })
            // User redirect is handled in the zAuthStack
        } catch (error: unknown) {
            Sentry.addBreadcrumb({ category: 'auth', message: 'verifyCode() error', level: 'error', data: { error: (error instanceof Error ? error.message : String(error)) } })
            const defaultMsg = "An issue occured while trying to verify your verification code."
            createToast({
                type: 'error',
                text: (typeof error === 'object' && error != null && 'errors' in error && Array.isArray(error.errors))
                    ? error.errors[0]?.longMessage || defaultMsg
                    : defaultMsg
            })
        }
    }

    async function activateSession(session?: string | null) {
        Sentry.addBreadcrumb({ category: 'auth', message: 'activateSession() called', level: 'info', data: { session, setActiveExists: setActive != null } })
        if (setActive == null) {
            Sentry.addBreadcrumb({ category: 'auth', message: 'activateSession() - setActive is null, returning early', level: 'warning' })
            return
        }
        try {
            await setActive({ session })
            Sentry.addBreadcrumb({ category: 'auth', message: 'setActive() completed successfully', level: 'info' })
        } catch (error) {
            Sentry.addBreadcrumb({ category: 'auth', message: 'setActive() failed', level: 'error', data: { error: (error as Error)?.message || String(error) } })
            throw error
        }
    }

    async function handleReSendCode(type: FormType = formType) {
        setCurrentCountDown(disableDuration)
        setOptSubmitEnabled(false)

        try {
            if (!isLoaded || !signIn || !signUp) throw new Error('Clerk has yet to load');
            switch (type) {
                case 'sms':
                    // Phone number is our only supported way of creating accounts. This may change in the future.
                    if (isNewUser && !!signUp.phoneNumber) await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' })
                    else if (pseudoUser.phoneNumber) await signIn.prepareFirstFactor({ strategy: 'phone_code', phoneNumberId: pseudoUser.phoneNumber.phoneNumberId })
                    else throw new Error('Unable to send SMS Verification without a phone number')
                    break;
                case 'email':
                    // Right now, Email is a secondary method of authentication and can only be used to sign in.
                    if (!isNewUser && pseudoUser.email) await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: pseudoUser.email.emailAddressId })
                    else throw new Error('Unable to send Email Verification without an email')
                    break;
                default: throw new Error(`Unsupported Type: ${type}`)
            }
            createToast({ type: 'success', text: 'Code Sent!' })
        } catch (error) {
            console.error(error)
            createToast({ type: 'error', text: 'Failed to send code' })
        }
    }

    function switchFormType(toForm: FormType = formType === 'sms' ? 'email' : 'sms') {
        handleReSendCode(toForm)
        setFormType(toForm)
    }

    return (<>
        <Column.Content className="auth-verify-screen">
            <img className="splitcard-logo" src="/splitcard/logo/white.png" />
            <div className='flex flex-col justify-center gap-4 w-full'>
                {(formType === 'sms') && (<>
                    {isNewUser
                        ? <h1 style={{ lineHeight: 1.15, marginBottom: '1rem' }}>Looks Like You're New!<br />Let's Verify Your Number</h1>
                        : <h1 style={{ lineHeight: 1.15, marginBottom: '1rem' }}>Please Enter Your Verification Code</h1>
                    }
                    <form id={formId} onSubmit={verifyCode}>
                        <OneTimePassword onChange={(e) => otpProps.onChange(e)} value={otp} />
                    </form>
                    {(pseudoUser.email != null) && <a onClick={() => { switchFormType() }} style={{ marginTop: '1rem' }}>Send to {pseudoUser.email.safeIdentifier || 'Email'} instead</a>}
                    <p style={{ marginTop: '1rem' }}>
                        {!optSubmitEnabled
                            ? <span>{`Resend in ${currentCountDown}s`}</span>
                            : <AnchorLink style={{ color: 'var(--splitcard-white)' }} onClick={() => handleReSendCode()}>Resend Code</AnchorLink>
                        }
                    </p>
                    <small style={{ marginTop: '1rem' }}>
                        <a onClick={() => window.open(links.contactUs, '_blank')}>Need Help?</a>
                    </small>
                </>)}
                {(formType === 'email' && pseudoUser.email != null) && (<>
                    <h1 style={{ lineHeight: 1.15, marginBottom: '1rem' }}>Please Enter Your Verification Code</h1>
                    <form id={formId} onSubmit={verifyCode}>
                        <OneTimePassword onChange={(e) => otpProps.onChange(e)} value={otp} />
                    </form>
                    <a onClick={() => { switchFormType() }} style={{ marginTop: '1rem' }}>Try phone number instead</a>
                    <p style={{ marginTop: '1rem' }}>
                        {!optSubmitEnabled
                            ? <span>{`Resend in ${currentCountDown}s`}</span>
                            : <AnchorLink style={{ color: 'var(--splitcard-white)' }} onClick={() => handleReSendCode()}>Resend Code</AnchorLink>
                        }
                    </p>
                    <small style={{ marginTop: '1rem' }}>
                        <a onClick={() => window.open(links.contactUs, '_blank')}>Need Help?</a>
                    </small>
                </>)}
            </div>
            <small>
                Message and data rates may apply. By continuing, you agree to
                our <a onClick={() => window.open(links.termsOfUse, '_blank')}>Terms of Service</a> & <a onClick={() => window.open(links.privacyPolicy, '_blank')}>Privacy Policy</a>
            </small>
            <div></div>
        </Column.Content>
        <Column.Footer>
            <Button style={{ width: '100%' }} theme="secondary" type="submit" form={formId} disabled={!isSubmitable}>Confirm</Button>
        </Column.Footer>
    </>)
}

const VerifyRoute: RouteObject = {
    path: "verify",
    element: <AuthVerifyScreen />,
}

export default VerifyRoute
